<script>
/**
 * Correct the receiving answers of a SKU (counted stock) or one unit, after
 * the fact.
 *
 * The questions are the ones the answers were given to - the server hands back
 * that script revision, not today's - and a record that was never answered is
 * offered today's script. Changing an answer can change which questions come
 * next (a "No" may jump ahead), so the server re-walks the answers on every
 * change and says which questions apply; answers to questions that drop off
 * the path are kept aside, so flipping an answer back loses nothing, but only
 * the questions on the path are saved.
 *
 * Saving goes through the same PUT the record's own edit uses; the server
 * rebuilds the stored set from the script revision, so the phrases cannot be
 * posted from here.
 */
import linkify, {stopLinkClick} from '@/lib/linkify'
import ListingBulletLines from './listing-bullet-lines.vue'

export default {
  name: 'listing-facts-edit',
  components: {ListingBulletLines},

  props: {
    itemId: {type: Number, required: true},
    // serialized stock: the unit whose answers these are
    unitId: {type: Number, default: null},
    // what the person sees in the title, e.g. the unit's or SKU's gli
    label: {type: String, default: ''},
  },

  data() {
    return {
      loaded: false,
      done: false,         // the first reply is in
      script: null,        // {id, name, version, version_id}
      body: null,
      fresh: false,        // never answered: today's questions
      upgraded: null,      // {from, to}: answered on an older revision, shown on the newest
      removed: [],         // [{q, prompt, answer}] answered, but the newest revision no longer asks
      added: [],           // question keys the newest revision added since the answers
      needsCategory: false,
      path: [],
      complete: false,
      answers: {},         // question key -> {a, free_text, skipped, items}
      stale: 0,            // request counter: only the newest reply counts
    }
  },

  computed: {
    url() {
      return `/api/item/${this.itemId}/answers`
    },

    query() {
      return this.unitId ? `?unit=${this.unitId}` : ''
    },

    steps() {
      return this.path.map(key => ({key, node: this.body.nodes[key]})).filter(s => s.node)
    },

    allValid() {
      return this.steps.length > 0 && this.steps.every(s => this.valid(s.key, s.node))
    },

    canSave() {
      return this.loaded && this.complete && this.allValid
    },
  },

  async mounted() {
    try {
      const r = await this.asyncAction(this.url + this.query, 'GET')

      this.needsCategory = !!r.needs_category
      this.script = r.script
      this.body = r.body
      this.fresh = !!r.fresh
      this.upgraded = r.upgraded || null
      this.removed = r.removed || []
      this.added = r.added || []
      this.path = r.path || []
      this.complete = !!r.complete

      ;(r.answers || []).forEach(a => this.$set(this.answers, a.q, {
        a: a.a === undefined ? null : a.a,
        free_text: a.free_text || '',
        skipped: !!a.skipped,
        items: Array.isArray(a.items) ? [...a.items] : [],
      }))

      this.ensure(this.path)
      this.loaded = !!r.script
      this.done = true
    } catch (e) {
      this.$emit('submit', false)
    }
  },

  methods: {
    linkify,
    stopLinkClick,

    // a question added in a later revision, or one that lost its answer in the change: it needs looking at
    flagged(key, node) {
      return !!this.upgraded && !this.valid(key, node)
    },

    // an entry for every question on the path, made here and not while rendering
    ensure(keys) {
      keys.forEach(k => {
        if (!this.answers[k])
          this.$set(this.answers, k, {a: null, free_text: '', skipped: false, items: []})
      })
    },

    state(key) {
      return this.answers[key]
    },

    why(key, node) {
      if (node.type === 'free')
        return 'Needs an answer.'

      const a = this.state(key).a

      return (Array.isArray(a) ? a.length : a) ? 'Needs a note.' : 'Pick an answer, or mark it skipped.'
    },

    picked(key, answerKey) {
      const a = this.state(key).a

      return Array.isArray(a) ? a.includes(answerKey) : a === answerKey
    },

    // 'none' | 'optional' | 'required' - the strictest of the picked answers
    noteMode(key, node) {
      const picked = (node.answers || []).filter(a => this.picked(key, a.key))
      const modes = picked.map(a => a.free_text || 'none')

      return modes.includes('required') ? 'required' : (modes.includes('optional') ? 'optional' : 'none')
    },

    valid(key, node) {
      const s = this.state(key)

      if (s.skipped)
        return true

      // a bullet list with no lines is an answer
      if (node.type === 'list')
        return true

      if (node.type === 'free')
        return !!s.free_text.trim()

      const chosen = Array.isArray(s.a) ? s.a.length : (s.a ? 1 : 0)

      return chosen > 0 && !(this.noteMode(key, node) === 'required' && !s.free_text.trim())
    },

    choose(key, node, answerKey) {
      const s = this.state(key)

      s.skipped = false

      if (node.type === 'single') {
        s.a = answerKey
      } else {
        const list = Array.isArray(s.a) ? [...s.a] : []
        const i = list.indexOf(answerKey)

        i === -1 ? list.push(answerKey) : list.splice(i, 1)
        s.a = list
      }

      // a note belongs to the answer it was typed with
      if (this.noteMode(key, node) === 'none' && node.type !== 'free')
        s.free_text = ''

      this.rewalk()
    },

    typed() {
      this.rewalk()
    },

    skip(key, on) {
      this.state(key).skipped = on
      this.rewalk()
    },

    row(key) {
      const s = this.state(key)

      return s.skipped
          ? {q: key, skipped: true}
          : (this.body.nodes[key] || {}).type === 'list'
            ? {q: key, a: null, items: (s.items || []).map(l => l.trim()).filter(Boolean)}
            : {q: key, a: s.a, free_text: s.free_text.trim() || null}
    },

    async rewalk() {
      const mine = ++this.stale

      const r = await this.asyncAction(this.url + '/path' + this.query, 'POST', {
        noload: true,
        data: {
          version: this.script.version_id,
          answers: Object.keys(this.answers).map(k => this.row(k)),
        },
      })

      // a newer change is already on its way; this reply is out of date
      if (mine !== this.stale)
        return

      this.ensure(r.path)
      this.path = r.path
      this.complete = !!r.complete
    },

    async save() {
      if (!this.canSave)
        return

      const url = this.unitId ? `/api/inventory/${this.unitId}` : `/api/item/${this.itemId}`

      await this.asyncAction(url, 'PUT', {
        data: {
          listing_facts: {
            version: this.script.version_id,
            answers: this.path.map(k => this.row(k)),
          },
        },
      })

      this.toast('Receiving answers saved' + (this.label ? ' for ' + this.label : ''))
      this.$emit('submit', true)
    },
  },
}
</script>

<template>
  <modal :title="'Receiving Answers' + (label ? ' - ' + label : '')" large height="640px">
    <template #footer>
      <b-button variant="outline-secondary" class="px-3 mr-2" size="sm" @click="$emit('submit', false)">Cancel</b-button>
      <b-button variant="primary" class="px-4" size="sm" :disabled="!canSave" @click="save">Save Answers</b-button>
    </template>

    <div class="mx-2 my-2" v-if="loaded">
      <div class="small text-muted mb-2">
        {{ script.name }} v{{ script.version }}
        <template v-if="fresh"> - never answered, these are the questions asked today.</template>
        <template v-else-if="upgraded"> - the newest questions.</template>
        <template v-else> - the questions as they were asked.</template>
      </div>

      <b-alert show variant="warning" class="py-2 small" v-if="upgraded">
        The script changed since these answers were given (v{{ upgraded.from }} &rarr; v{{ upgraded.to }}).
        Answers that still fit are carried over.
        <template v-if="steps.some(s => flagged(s.key, s.node))">Questions marked <b>New</b> or highlighted need an answer.</template>
        <template v-if="removed.length"> Answers to questions that were removed are shown greyed at the bottom; saving drops them.</template>
      </b-alert>

      <b-alert show variant="info" class="py-2 small" v-if="unitId">
        Saving re-decides this unit's condition from the answers. Listing copy that is already written
        stays as it is until it is generated again.
      </b-alert>
      <b-alert show variant="info" class="py-2 small" v-else>
        Listing copy that is already written stays as it is until it is generated again.
      </b-alert>

      <div v-for="(s, i) in steps" :key="s.key" class="border rounded p-2 mb-2" :class="{'facts-needs': flagged(s.key, s.node)}">
        <div class="d-flex align-items-start">
          <div class="mr-auto">
            <div class="small text-muted">
              Question {{ i + 1 }}
              <b-badge variant="warning" class="ml-1" v-if="upgraded && added.includes(s.key)">New</b-badge>
            </div>
            <div class="font-weight-bold">{{ s.node.prompt }}</div>
            <div class="small text-muted" v-if="s.node.help" v-html="linkify(s.node.help)" @click="stopLinkClick"/>
          </div>
          <b-form-checkbox switch class="small text-nowrap" :checked="state(s.key).skipped" @change="skip(s.key, $event)">
            Skipped
          </b-form-checkbox>
        </div>

        <template v-if="!state(s.key).skipped">
          <listing-bullet-lines v-if="s.node.type === 'list'" :value="state(s.key).items" :lead="s.node.lead || s.node.prompt"
                                @input="v => { state(s.key).items = v; typed() }"/>

          <b-list-group class="mt-2" v-else-if="s.node.type !== 'free'">
            <b-list-group-item v-for="a in s.node.answers" :key="a.key" button class="py-1 d-flex align-items-center"
                               :active="picked(s.key, a.key)" @click="choose(s.key, s.node, a.key)">
              <font-awesome-icon class="mr-3"
                                 :icon="s.node.type === 'multi'
                                   ? (picked(s.key, a.key) ? 'fa-solid fa-square-check' : 'fa-regular fa-square')
                                   : (picked(s.key, a.key) ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle')"/>
              <div>
                <div>{{ a.label }}</div>
                <div class="small" :class="picked(s.key, a.key) ? '' : 'text-muted'" v-if="a.description"
                     v-html="linkify(a.description)" @click="stopLinkClick"/>
              </div>
            </b-list-group-item>
          </b-list-group>

          <b-textarea v-if="s.node.type === 'free' || (s.node.type !== 'list' && noteMode(s.key, s.node) !== 'none')" v-model="state(s.key).free_text"
                      size="sm" rows="2" class="mt-2" debounce="400" @input="typed"
                      :placeholder="s.node.type === 'free' ? 'Your answer' : (noteMode(s.key, s.node) === 'required' ? 'Note (required)' : 'Note (optional)')"/>

          <div class="small text-danger mt-1" v-if="!valid(s.key, s.node)">
            {{ why(s.key, s.node) }}
          </div>
        </template>
      </div>

      <div class="small text-warning" v-if="!complete && allValid">The questions are not finished - answer the last one shown.</div>

      <!-- answered on the older revision, no longer asked: shown for reference only -->
      <template v-if="removed.length">
        <div class="small text-muted mt-3 mb-1">Removed from the script - for reference, not saved</div>
        <div v-for="g in removed" :key="'gone-' + g.q" class="border rounded p-2 mb-2 facts-removed">
          <div class="font-weight-bold">{{ g.prompt }}</div>
          <div class="small">{{ g.answer || 'No answer' }}</div>
        </div>
      </template>
    </div>

    <div class="m-3 text-muted" v-else-if="needsCategory">
      Set the eBay category or product on the item first - the questions are picked from what the item is.
    </div>
    <div class="m-3 text-muted" v-else>
      {{ done ? 'No receiving script applies to this item.' : 'Loading...' }}
    </div>
  </modal>
</template>

<style scoped>
/* a question to look at after the script changed */
.facts-needs { border-color: var(--warning, #ffc107) !important; background: rgba(255, 193, 7, .08); }
/* answered, but the newest script no longer asks it */
.facts-removed { opacity: .55; background: rgba(0, 0, 0, .03); }
</style>
